import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { throttle } from 'es-toolkit';
import { createContext, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { createPortal } from 'react-dom';
import type { HTMLTagProps } from '@/types';
interface Location {
  top: number;
  left: number;
}
enum OPENING_TRANSITION {
  OPENING = 1,
  OPENED = 2,
  CLOSING = 3,
  CLOSED = 0,
}
interface TooltipContextValue {
  locationState: [Location, ReturnType<typeof throttle>];
  tooltipState: [OPENING_TRANSITION, (status: boolean, runInstantly?: boolean) => void];
}
const TooltipContext = createContext<TooltipContextValue | undefined>(undefined);
export interface TooltipContextProviderProps {
  children: React.ReactNode;
  mouseEnterDelay?: number;
}
const locationReducer = (_: Location, event: Location) => ({
  left: event.left,
  top: event.top + 10
});
const CLOSING_DURATION = 100;
const TooltipContextProvider = t0 => {
  const $ = _c(22);
  const {
    children,
    mouseEnterDelay: t1
  } = t0;
  const mouseEnterDelay = t1 === undefined ? 200 : t1;
  const [status, setStatus] = useState(OPENING_TRANSITION.CLOSED);
  let t2;
  let t3;
  if ($[0] !== mouseEnterDelay || $[1] !== status) {
    t2 = () => {
      switch (status) {
        case OPENING_TRANSITION.CLOSING:
          {
            const closeTimer = setTimeout(() => {
              setStatus(OPENING_TRANSITION.CLOSED);
            }, CLOSING_DURATION);
            return () => clearTimeout(closeTimer);
          }
        case OPENING_TRANSITION.OPENING:
          {
            const openTimer = setTimeout(() => setStatus(OPENING_TRANSITION.OPENED), mouseEnterDelay);
            return () => clearTimeout(openTimer);
          }
      }
    };
    t3 = [mouseEnterDelay, setStatus, status];
    $[0] = mouseEnterDelay;
    $[1] = status;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      left: 0,
      top: 0
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [location, dispatchLocation] = useReducer(locationReducer, t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = throttle(dispatchLocation, 100);
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== mouseEnterDelay) {
    t6 = [t5, (status_0, runInstantly) => {
      if (status_0) {
        if (mouseEnterDelay && !runInstantly) {
          setStatus(OPENING_TRANSITION.OPENING);
        } else {
          setStatus(OPENING_TRANSITION.OPENED);
        }
      } else {
        if (CLOSING_DURATION && !runInstantly) {
          setStatus(OPENING_TRANSITION.CLOSING);
        } else {
          setStatus(OPENING_TRANSITION.CLOSED);
        }
      }
    }];
    $[6] = mouseEnterDelay;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const [throttledDispatchLocation, handleStatus] = t6;
  let t7;
  if ($[8] !== location || $[9] !== throttledDispatchLocation) {
    t7 = [location, throttledDispatchLocation];
    $[8] = location;
    $[9] = throttledDispatchLocation;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = t7 as const;
  let t9;
  if ($[11] !== handleStatus || $[12] !== status) {
    t9 = [status, handleStatus];
    $[11] = handleStatus;
    $[12] = status;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = t9 as const;
  let t11;
  if ($[14] !== t10 || $[15] !== t8) {
    t11 = {
      locationState: t8,
      tooltipState: t10
    };
    $[14] = t10;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const tooltipContextValue = t11 satisfies TooltipContextValue;
  let t12;
  if ($[17] !== children) {
    t12 = <>{children}</>;
    $[17] = children;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== tooltipContextValue) {
    t13 = <TooltipContext.Provider value={tooltipContextValue}>{t12}</TooltipContext.Provider>;
    $[19] = t12;
    $[20] = tooltipContextValue;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  return t13;
};
const useTooltipContext = () => {
  const context = useContext(TooltipContext);
  if (context === undefined) {
    throw new Error("useTooltipContext must be used within a TooltipContextProvider");
  }
  return context;
};
type TooltipAreaProps = HTMLTagProps<'div'>;
const TooltipArea = t0 => {
  const $ = _c(23);
  let className;
  let onMouseLeave;
  let onMouseMove;
  let restDivProps;
  if ($[0] !== t0) {
    ({
      className,
      onMouseMove,
      onMouseLeave,
      ...restDivProps
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseLeave;
    $[3] = onMouseMove;
    $[4] = restDivProps;
  } else {
    className = $[1];
    onMouseLeave = $[2];
    onMouseMove = $[3];
    restDivProps = $[4];
  }
  const {
    tooltipState: t1,
    locationState: t2
  } = useTooltipContext();
  const [, setOpeningTransition] = t1;
  const [, dispatchLocation] = t2;
  let t3;
  let t4;
  if ($[5] !== setOpeningTransition) {
    t3 = () => {
      const scrollEventHandler = () => setOpeningTransition(false, true);
      document.addEventListener("scroll", scrollEventHandler);
      return () => document.removeEventListener("scroll", scrollEventHandler);
    };
    t4 = [setOpeningTransition];
    $[5] = setOpeningTransition;
    $[6] = t3;
    $[7] = t4;
  } else {
    t3 = $[6];
    t4 = $[7];
  }
  useEffect(t3, t4);
  let t5;
  if ($[8] !== className) {
    t5 = cn("w-fit cursor-help", className);
    $[8] = className;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== dispatchLocation || $[11] !== onMouseLeave || $[12] !== setOpeningTransition) {
    t6 = e => {
      setOpeningTransition(false);
      onMouseLeave?.(e);
      dispatchLocation({
        left: e.clientX,
        top: e.clientY
      });
    };
    $[10] = dispatchLocation;
    $[11] = onMouseLeave;
    $[12] = setOpeningTransition;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== dispatchLocation || $[15] !== onMouseMove || $[16] !== setOpeningTransition) {
    t7 = e_0 => {
      setOpeningTransition(true);
      dispatchLocation({
        left: e_0.clientX,
        top: e_0.clientY
      });
      onMouseMove?.(e_0);
    };
    $[14] = dispatchLocation;
    $[15] = onMouseMove;
    $[16] = setOpeningTransition;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== restDivProps || $[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = <div {...restDivProps} className={t5} onMouseLeave={t6} onMouseMove={t7} />;
    $[18] = restDivProps;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
interface TooltipContentProps extends Omit<HTMLTagProps<'div'>, 'styles'> {
  align?: 'start' | 'center' | 'end';
}
const TooltipContent = t0 => {
  const $ = _c(19);
  let children;
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let restDivProps;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      className,
      onMouseEnter,
      onMouseLeave,
      align: t1,
      style,
      ...restDivProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = onMouseEnter;
    $[4] = onMouseLeave;
    $[5] = restDivProps;
    $[6] = style;
    $[7] = t1;
  } else {
    children = $[1];
    className = $[2];
    onMouseEnter = $[3];
    onMouseLeave = $[4];
    restDivProps = $[5];
    style = $[6];
    t1 = $[7];
  }
  const align = t1 === undefined ? "center" : t1;
  const {
    tooltipState: t2,
    locationState: t3
  } = useTooltipContext();
  const [openingTransition, setOpeningTransition] = t2;
  const [location] = t3;
  let t4;
  if ($[8] !== align || $[9] !== children || $[10] !== className || $[11] !== location || $[12] !== onMouseEnter || $[13] !== onMouseLeave || $[14] !== openingTransition || $[15] !== restDivProps || $[16] !== setOpeningTransition || $[17] !== style) {
    t4 = openingTransition ? createPortal(<div {...restDivProps} className={cn("fixed z-10 flex flex-col duration-300 animate-in fade-in-0", align === "center" && "items-center", align === "start" && "items-start", align === "end" && "items-end", openingTransition === OPENING_TRANSITION.CLOSING && "animate-out fade-out-0")} style={{
      left: location.left,
      top: location.top
    }} onMouseEnter={e => {
      setOpeningTransition(true);
      onMouseEnter?.(e);
    }} onMouseLeave={e_0 => {
      setOpeningTransition(false);
      onMouseLeave?.(e_0);
    }}><div className="absolute z-[1] size-0 border-x-[7px] border-b-8 border-solid border-transparent border-b-[rgba(74,77,85,0.8)]" /><div className={cn("absolute z-[2] mt-8 w-fit whitespace-pre rounded-xsmall bg-[rgba(74,77,85,0.8)] p-12 px-16 text-[0.875rem] text-Gray-white", align === "start" && "-left-8", align === "end" && "-right-8", className)} style={style}>{children}</div></div>, document.body) : null;
    $[8] = align;
    $[9] = children;
    $[10] = className;
    $[11] = location;
    $[12] = onMouseEnter;
    $[13] = onMouseLeave;
    $[14] = openingTransition;
    $[15] = restDivProps;
    $[16] = setOpeningTransition;
    $[17] = style;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  return t4;
};
export const Tooltip = Object.assign(TooltipContextProvider, {
  Area: TooltipArea,
  Content: TooltipContent
});